import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { ClientAvatar } from "@/components/ClientAvatar";
import { PropertyThumb } from "@/components/PropertyThumb";
import { PaymentBadge } from "@/components/Badges";
import { formatCurrency, formatDate } from "@/lib/constants";
import { ArrowLeft, Mail, Phone, MapPin, Loader2, CalendarClock, ChevronRight, ClipboardList, DollarSign, AlertCircle } from "lucide-react";
import { toast } from "sonner";

function Stat({ icon: Icon, label, value, tint }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={`client-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg" style={{ background: `${tint}22` }}>
          <Icon className="h-3.5 w-3.5" style={{ color: tint }} />
        </div>
        <span className="overline text-muted-foreground">{label}</span>
      </div>
      <div className="mt-2 font-heading text-2xl font-extrabold tracking-tight">{value}</div>
    </div>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/clients/${encodeURIComponent(id)}`)
      .then(({ data }) => setClient(data))
      .catch((err) => { toast.error(apiError(err)); navigate("/clients"); })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) return <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (!client) return null;

  return (
    <div className="mx-auto max-w-5xl p-6 md:p-8">
      <button onClick={() => navigate("/clients")} data-testid="back-to-clients-btn"
        className="mb-6 flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to clients
      </button>

      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center" data-testid="client-detail-header">
        <div className="flex items-center gap-3">
          <ClientAvatar name={client.name} email={client.email} className="h-16 w-16 text-lg" testId="client-detail-avatar" />
          {client.logo && <img src={client.logo} alt={`${client.name} logo`} className="h-16 w-16 shrink-0 rounded-lg bg-secondary object-contain p-1.5" data-testid="client-detail-logo" />}
        </div>
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-bold tracking-tight" data-testid="client-name">{client.name}</h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
            {client.email && (
              <a href={`mailto:${client.email}`} className="flex items-center gap-1.5 hover:text-foreground" data-testid="client-email">
                <Mail className="h-3.5 w-3.5" />{client.email}
              </a>
            )}
            {client.phone && (
              <a href={`tel:${client.phone}`} className="flex items-center gap-1.5 hover:text-foreground" data-testid="client-phone">
                <Phone className="h-3.5 w-3.5" />{client.phone}
              </a>
            )}
            <span className="flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" />Last order {formatDate(client.last_order_at)}</span>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat icon={ClipboardList} label="Properties" value={client.orders_count} tint="#3B82F6" />
        <Stat icon={DollarSign} label="Lifetime Spend" value={formatCurrency(client.total_spend, client.currency)} tint="#10B981" />
        <Stat icon={AlertCircle} label="Unpaid" value={formatCurrency(client.unpaid_amount, client.currency)} tint="#F43F5E" />
      </div>

      {/* Properties */}
      <div className="mt-8 rounded-xl border border-border bg-card" data-testid="client-properties">
        <div className="border-b border-border px-5 py-4">
          <span className="overline text-muted-foreground">Linked Properties</span>
        </div>
        <div className="divide-y divide-border">
          {(client.properties || []).length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-muted-foreground">No properties for this client yet.</div>
          ) : (
            client.properties.map((p) => (
              <button key={p.order_id} onClick={() => navigate(`/orders/${p.order_id}`)}
                data-testid={`client-property-${p.order_id}`}
                className="group flex w-full items-center justify-between gap-3 px-5 py-3.5 text-left transition-colors hover:bg-secondary/40">
                <PropertyThumb orderId={p.order_id} show={p.has_thumb} className="h-12 w-16" testId={`client-property-thumb-${p.order_id}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" />
                    <span className="truncate text-sm font-medium">{p.address}</span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 pl-5 text-xs text-muted-foreground">
                    <span className="font-mono">#{p.number}</span>
                    {p.shoot_date && <span className="flex items-center gap-1"><CalendarClock className="h-3 w-3" />{formatDate(p.shoot_date)}</span>}
                    <span>{p.deliverables_delivered}/{p.deliverables_total} products done</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <PaymentBadge status={p.payment_status} />
                  <span className="hidden font-mono text-sm sm:inline">{formatCurrency(p.total_amount, p.currency)}</span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
